import classNames from "classnames";
import InputSearch from "components/base/input-search/input-search";
import CmsTable from "components/partial/cms/cms-table/cms-table";
import LoadingIndicator from "components/partial/loading-indicator/loading-indicator";
import isEmpty from "lodash/isEmpty";
import { IGetRecordsResponse } from "models/dto/ZoomiLxp/Models/Common/IGetRecordsResponse";
import { IGroupProfileItemModel } from "models/dto/ZoomiLxp/Models/GroupModels/IGroupProfileItemModel";
import { IQueryParams } from "models/dto/ZoomiLxp/Models/Query/IQueryParams";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTable } from "react-table";
import { GroupsApi } from "api/controllers/GroupsApi";
import styles from "./styles.module.scss";
import { useAppStore } from "store";
import InfiniteScroll from "react-infinite-scroll-component";
import {
	Columns,
	getInitialParams,
	GROUPS_CERTIFICATIONS_PAGE_SIZE,
	GroupsCertificationsTab,
	groupsCertificationsTabs,
} from "./data";
import MyGroupsCertificationsTile from "./my-groups-certifications-tile";
import { useRouteUserId } from "hooks/useRouteUserId";
import { SiteSettingsKeys } from "models/dto/ZoomiLxp/Utilities/Constants/SiteSettingsKeys";
import { observer } from "mobx-react";

interface GroupsCertificationsTableProps {
	activeTab: GroupsCertificationsTab;
	title: string;
	accountId?: number;
}

const MyGroupsCertificationsTable = ({
	activeTab,
	title,
	accountId: initialAccountId,
}: GroupsCertificationsTableProps) => {
	const { toasterStore } = useAppStore();
	const tableColumns = useMemo(() => Columns(), []);
	const [data, setData] = useState<IGroupProfileItemModel[]>([]);
	const [total, setTotal] = useState<number>(0);
	const [loading, setLoading] = useState<boolean>(false);
	const [queryString, setQueryString] = useState<string>("");
	const params = useRef<IQueryParams>(getInitialParams());

	const table = useTable<IGroupProfileItemModel>({
		columns: tableColumns,
		data,
	});

	const getData = useCallback(
		async (queryParams: IQueryParams): Promise<IGetRecordsResponse<IGroupProfileItemModel>> => {
			const onlyCertifications = activeTab === GroupsCertificationsTab.MyGroupsCertifications;
			const response = initialAccountId
				? await GroupsApi.getUserGroupsProfile(initialAccountId, queryParams, onlyCertifications)
				: await GroupsApi.getMyGroupsProfile(queryParams, onlyCertifications);
			return response.data.data;
		},
		[activeTab, initialAccountId]
	);

	const loadInitialData = useCallback(async () => {
		setLoading(true);
		params.current = {
			...params.current,
			queryString,
			skip: 0,
			take: GROUPS_CERTIFICATIONS_PAGE_SIZE,
		};

		try {
			const response = await getData(params.current);
			setData(response.records);
			setTotal(response.totalFilteredRecords);
		} catch (error) {
			toasterStore.showErrorMessage(error as Error);
		} finally {
			setLoading(false);
		}
	}, [getData, queryString, toasterStore]);

	useEffect(() => {
		loadInitialData();
	}, [loadInitialData]);

	const fetchMoreData = async () => {
		if (loading || data.length >= total) return;

		setLoading(true);
		params.current = {
			...params.current,
			queryString,
			skip: data.length,
			take: GROUPS_CERTIFICATIONS_PAGE_SIZE,
		};

		try {
			const response = await getData(params.current);
			setData((prevState) => prevState.concat(response.records));
			setTotal(response.totalFilteredRecords);
		} catch (error) {
			toasterStore.showErrorMessage(error as Error);
		} finally {
			setLoading(false);
		}
	};

	const handleSearch = (queryString: string) => {
		setQueryString(queryString);
	};

	const getKey = (item: IGroupProfileItemModel) => `${activeTab}-${item.id}`;
	const hasMore = data.length < total;

	return (
		<>
			<div className={styles.groups_certifications__table_header}>
				<div className={styles.groups_certifications__table_title}>{title}</div>
				<InputSearch
					defaultValue={queryString}
					onChange={handleSearch}
					disabled={loading}
					className={styles.groups_certifications__search}
				/>
			</div>
			<div className={styles.groups_certifications__body}>
				<LoadingIndicator loading={loading}>
					{!isEmpty(data) ? (
						<InfiniteScroll
							dataLength={data.length}
							next={fetchMoreData}
							hasMore={hasMore}
							loader={<h4>Loading...</h4>}
						>
							<CmsTable
								table={table}
								data={data}
								tileComponent={MyGroupsCertificationsTile}
								getKey={getKey}
								className={styles.groups_certifications__table}
								disableRowCursor
								disableRowHover
							/>
							<div className={styles.groups_certifications__scroll_footer} />
						</InfiniteScroll>
					) : (
						!loading && <div className={styles.groups_certifications__no_data}>There is no data yet</div>
					)}
				</LoadingIndicator>
			</div>
		</>
	);
};

const MyGroupsCertifications = () => {
	const { routeUserId } = useRouteUserId();
	const { settingsStore } = useAppStore();
	const [activeTab, setActiveTab] = useState<GroupsCertificationsTab>(GroupsCertificationsTab.AllGroups);
	const accountId = routeUserId || undefined;
	const pagesSettings = settingsStore.publicSettings.Pages;
	const title = pagesSettings?.[SiteSettingsKeys.MyDataGroupsCertificationsTitle] ?? "Groups (Certifications)";
	const tabNames: Record<GroupsCertificationsTab, string> = {
		[GroupsCertificationsTab.AllGroups]:
			pagesSettings?.[SiteSettingsKeys.MyDataGroupsCertificationsAllGroupsTabName] ?? "All Groups",
		[GroupsCertificationsTab.MyGroupsCertifications]:
			pagesSettings?.[SiteSettingsKeys.MyDataGroupsCertificationsMyGroupsTabName] ?? "My Groups (Certifications)",
	};

	return (
		<div className={styles.groups_certifications}>
			<div className={styles.groups_certifications__header}>
				<div className={styles.groups_certifications__title}>{title}</div>
			</div>
			<div className={styles.groups_certifications__tabs} role="tablist" aria-label="Groups certifications tabs">
				{groupsCertificationsTabs.map((tab) => (
					<button
						key={tab}
						type="button"
						role="tab"
						aria-selected={activeTab === tab}
						className={classNames(styles.groups_certifications__tab, {
							[styles.groups_certifications__tab_active]: activeTab === tab,
						})}
						onClick={() => setActiveTab(tab)}
					>
						{tabNames[tab]}
					</button>
				))}
			</div>
			<MyGroupsCertificationsTable
				key={`${activeTab}-${accountId}`}
				activeTab={activeTab}
				title={tabNames[activeTab]}
				accountId={accountId}
			/>
		</div>
	);
};

export default observer(MyGroupsCertifications);
